import React, {useEffect, useRef, useState, useMemo} from 'react';
import {injectIntl} from '@s7ui-kit/s7ui-kit';
import {connect} from 'react-redux';
import Cookie from 'js-cookie';
import {format} from 'date-fns';

import Button from '../common/Button';
import getResizeImageUrl from '../../services/utils/getResizeImageUrl';
import pushGA from '../../services/utils/pushGA';
import { loadPartnersOffers } from '../../services/services/AdsService';
import { ShowMore } from './ShowMore';
import useIntersectionObserver from  '../../hooks/useIntersection';
import { CDN_PATH } from '../../constants/variables';
import {
    analyticsMainPageBlockClick,
    analyticsMainPageBlockLoaded,
    analyticsMainPageBlockShown
} from '../../services/analytics/analyticsMainpage';

import ActionButton from '../../assets/icon/Subtract.svg';

import './recommendations-cards.scss';

const mapStateToProps = ({App, User}) => ({App, User});

const RecommendationsCards = props => {
    const {App: {lang, deviceWidth}, User: {profile}} = props;

    const {limit, trip, words, getMessage} = props;
    const [recommendations, setRecommendations] = useState([]);
    const [emptyCardsCount, setEmptyCardsCount] = useState(0);
    const [isRecommendationsLoaded, setIsRecommendationsLoaded] = useState(false);
    const recommendationsBlock = useRef();

    const entry = useIntersectionObserver(recommendationsBlock, { freezeOnceVisible: false });
    const isVisible = !!entry?.isIntersecting;
    let alreadySent = useRef(false);

    useEffect(() => {
        if(recommendations.length !== 0 && isVisible && !alreadySent?.current){
            alreadySent.current = true;

            setTimeout(() => {
                analyticsMainPageBlockShown({
                    step: 'marketplace',
                    items: ecomParamsForGA,
                    ux: 'auto_open'
                })
            }, 2000);
        }
    }, [ entry ]);

    const ecomParamsForGA = useMemo(() => {
        return recommendations.map((recommendation, index) => {
            return {
                item_id: recommendation.content1,
                item_name: 'marketplace',
                affiliation: 'marketplace',
                index: index + 1,
                location_id: 's7_main'
            }
        })
    }, [ recommendations ]);

    useEffect(() => {
        getRecommendations();
    }, [ trip ]);

    const getRecommendations = () =>{
        const options = {
            deviceId: Cookie.get('ssdkl'),
            lang: lang,
            limit: limit,
            profileId: profile?.id || '',
            standing: 's7site',
            pnr: trip.pnr,
        };

        loadPartnersOffers({ body: JSON.stringify(options) })
            .then((response) => {
                if (!response || !Array.isArray(response)) {
                    return;
                }

                const sortedData = response.sort((a,b) => {
                    return b.score - a.score
                });

                setRecommendations(sortedData.slice(0, 3));
            }).catch(e => {
                console.log(e)
            });


        if(limit > 0 && limit < 3) {
            setEmptyCardsCount(4 - limit);
        }
    };

    const onClickCard = ({item, index}) => {
        analyticsMainPageBlockClick({
            step: 'marketplace',
            items: [{
                item_id: item,
                item_name: 'marketplace',
                affiliation:  'marketplace',
                index: index ? index + 1 : undefined,
                location_id: 's7_main'
            }]
        });
    };

    useEffect(() => {
        pushLoadedGA();

        if(recommendations.length !== 0){
            analyticsMainPageBlockLoaded({
                step: 'marketplace',
                items: ecomParamsForGA
            });
        }

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        }
    }, [recommendations]);

    const getCorrectLastName = () => {
        let correctLastName = '';

        if(!profile || !profile.names) {
            return '';
        }

        profile.names.map((item) => {
            if(item.lang === 'en') {
                correctLastName = item.lastName
            }
        });

        return correctLastName;
    }

    const renderEmptyCards = () => {
        const emptyCardsArray = Array.from({length: emptyCardsCount}, (v, i) => i);
        const correctLastName = getCorrectLastName();
        const buttonLink = `https://unicorn.s7.ru/?tab=next-flight&pnr=${trip.pnr}${correctLastName ? `&lastName=${correctLastName}` : ''}&utm_source=s7site&utm_campaign=main&utm_medium=direct`;
        return (
            emptyCardsArray.map((index) => {
                return (
                    <div
                        key={`empty-card-${index}`}
                        className={'recommendations__item recommendations__item--empty item-empty'}
                    >
                        <img src={CDN_PATH + 'S7PCabinService/media/by-path/raw/images/stayhome/logo-s7.svg'}
                             alt='Логотип S7'
                             className={'item-empty__logo'}
                        />
                        <div className={'item-empty__title'} dangerouslySetInnerHTML={{
                            __html: getMessage(words, 'com.s7.main.page.recommendations.empty.title')
                        }}/>
                        <a className={'item-empty__button'} href={buttonLink}>
                            <Button
                                size={'medium'}
                                type={'b2c-primary'}
                                text={'Выбрать'}
                                onClick={() => {
                                    onClickCard({item: 'all'});
                                    pushRecommendationsGA('Click Show More', recommendations.length, 0)
                                }}
                            />
                        </a>
                    </div>
                )
            })
        )
    };

    const getCardBackgroundUrl = (pictureUrl) => {
        if(pictureUrl.includes('http://') || pictureUrl.includes('https://')) {
            return pictureUrl;
        } else {
            return getResizeImageUrl({
                typeResize: 'crop',
                width: '320',
                height: '200',
                imageCabinId: pictureUrl
            })
        }
    };

    const getCorrectDefaultCardLink = (recommendation, outLink) => {
        let clearOutLink = new URL(outLink);

        if(outLink.includes('?')) {
            clearOutLink = new URL(outLink.slice(0, outLink.indexOf('?')));
        }

        clearOutLink.searchParams.append('lang', lang);
        if (trip && trip.segment) {
            clearOutLink.searchParams.append('iata', trip.destinationIata);
            clearOutLink.searchParams.append('checkin_monthday', format(new Date(trip.segment.departureDate), 'd'));
            clearOutLink.searchParams.append('checkin_year_month', format(new Date(trip.segment.departureDate), 'YYYY-MM'));
            clearOutLink.searchParams.append('checkout_monthday', format(new Date(trip.segment.arrivalDate), 'd'));
            clearOutLink.searchParams.append('checkout_year_month', format(new Date(trip.segment.arrivalDate), 'YYYY-MM'));
        }

        return clearOutLink;
    }

    const pushRecommendationsGA = (eventAction, eventValue, eventInteraction, mrtplc) => {
        const checkin = trip.checkinOpen ? trip.checkinOpen : '';
        const spnr = trip.spnr ? trip.spnr : '';
        const pnr = trip.pnr ? trip.pnr : '';
        const departureAirport = trip.segment.departureAirport && trip.segment.departureAirport.code ? trip.segment.departureAirport.code : '';
        const arrivalAirport = trip.segment.arrivalAirport && trip.segment.arrivalAirport.code ? trip.segment.arrivalAirport.code : '';
        const daysLeftToFlight = trip.segment.daysToFlight ? trip.segment.daysToFlight : '';

        const recommendationsTypeArray = recommendations.map((item) => {
            return item.category ? item.category.toLowerCase() : ''
        });

        pushGA(
            'PORTAL UnicornRecommendations',
            eventAction,
            `CHECKIN:${checkin};ORDERID:${spnr};PNR:${pnr}`,
            {
                eventValue: eventValue,
                flightDetail: `FROM:${departureAirport};TO:${arrivalAirport};LEFT:${daysLeftToFlight};MRTPLC:${mrtplc ? mrtplc : recommendationsTypeArray.join()}`,
                eventInteraction: eventInteraction,
                eventUX: undefined
            }
        )
    }

    const pushLoadedGA = () => {
        if(recommendations.length > 0 && !isRecommendationsLoaded) {
            setIsRecommendationsLoaded(true)
            pushRecommendationsGA('Load Offers', recommendations.length, 1)
        }
    }

    const handleScroll = () => {
        if (recommendationsBlock.current && recommendationsBlock.current.offsetTop && window.pageYOffset >= (recommendationsBlock.current.offsetTop - window.innerHeight) + recommendationsBlock.current.clientHeight) {
            pushRecommendationsGA('Show Offers', recommendations.length, 1);
            window.removeEventListener('scroll', handleScroll);
        }
    }

    return (
        recommendations &&
            <div className={'recommendations-wrapper'}>
                {
                    (emptyCardsCount === 0 || deviceWidth < 767) &&
                    <ShowMore
                        text={getMessage(words, 'com.s7.main.page.all.recommendations')}
                        link={`https://marketplace.s7.ru/trip?utm_source=s7site${!profile && getCorrectLastName() ? `&lastName=${getCorrectLastName()}` : ''}${!profile && trip.pnr ? `&pnr=${trip.pnr}` : ''}&utm_campaign=main&utm_medium=direct`}
                        className={'recommendations__header'}
                        onClick={() => {
                            onClickCard({item: 'all'});
                            pushRecommendationsGA('Click Show More', recommendations.length, 0)
                        }}
                    />
                }
                <div
                    className={'recommendations'}
                    ref={recommendationsBlock}
                >
                    {
                        recommendations.map((recommendation, index) => {
                            const pictureUrl = recommendation.picture1 && recommendation.picture1.url;

                            return (
                                <div key={`${recommendation.score}${recommendation.code}`} className={'recommendations__item'}
                                     style={pictureUrl && {backgroundImage: `url(${getCardBackgroundUrl(pictureUrl)})`}}
                                >
                                    <div
                                        className={'recommendations__item-title'}
                                        dangerouslySetInnerHTML={{
                                            __html: recommendation.content1
                                        }}
                                    />
                                    <div
                                        className={'recommendations__item-desc'}
                                        dangerouslySetInnerHTML={{
                                            __html: recommendation.content2
                                        }}
                                    />
                                    <a
                                        href={getCorrectDefaultCardLink(recommendation ,recommendation.outLink)}
                                        className={'recommendations__item-button'}
                                        onClick={() => {
                                            pushRecommendationsGA('Click Offer', 0, 0, recommendation.category ? recommendation.category : undefined);
                                            onClickCard({item: recommendation.content1, index})
                                        }}
                                    >
                                        <ActionButton />
                                        <span className={'recommendations__item-button-title'}>
                                            {recommendation.content4}
                                        </span>
                                    </a>
                                    {pictureUrl && <div className={'recommendations__item-shadow'} />}
                                </div>
                            )
                        })
                    }
                    {
                        (emptyCardsCount > 0 && deviceWidth > 767) &&
                        renderEmptyCards()
                    }
                </div>
            </div>
    )
};

export default injectIntl(connect(mapStateToProps)(React.memo(RecommendationsCards)));
